import {
  ElementRef,
  FC,
  ForwardedRef,
  ReactNode,
  forwardRef,
  useState,
  useEffect,
} from "react";
import AsyncSelect from "react-select/async";
import _uniqueId from "lodash/uniqueId";

import { getOptionsMaxHeight } from "./dropdown.utils";
import { useCounterPill } from "./useCounterPill";
import _debounce from "lodash/debounce";

export enum AsyncDropdownTestIds {
  Component = "async-dropdown",
}
import { Label, Asterisk, ErrorMessage } from "./Dropdown";
import {
  Control,
  DropdownContextProvider,
  DropdownIndicator,
  Group,
  GroupHeading,
  IndicatorsContainer,
  Input,
  Menu,
  MenuList,
  Option as OptionComponent,
  Placeholder,
  MultiValue,
  ValueContainer,
} from "./components";
import { Option, DropdownVariant, MultiSelectDropdownProps } from "./types";
import { NoOptionsMessage } from "./components/NoOptionsMessage";
import { ClearIndicator } from "./components/ClearIndicator";
import { useDropdown } from "./useDropdown";

const multiSelectOverrideComponents = {
  ClearIndicator,
  Control,
  DropdownIndicator,
  IndicatorsContainer,
  Group,
  GroupHeading,
  Input,
  Menu,
  MenuList,
  NoOptionsMessage,
  Option: OptionComponent,
  Placeholder,
  MultiValue,
  ValueContainer,
};

type AsyncDropdownContainerProps = {
  id: string;
  testId?: string;
  label?: string;
  labelId?: string;
  variant: DropdownVariant;
  isRequired: boolean;
  errorMessage: string;
  allowIcons: boolean;
  hasSelectedOption?: boolean;
  children: ReactNode;
  name: string;
  cachedOptions?: Option[];
};

export const AsyncDropdownContainer: FC<AsyncDropdownContainerProps> = ({
  id,
  name,
  testId,
  label,
  labelId,
  variant,
  isRequired,
  errorMessage,
  allowIcons,
  hasSelectedOption,
  children,
}) => {
  const hasError = errorMessage.length > 0;

  return (
    <div id={id} data-testid={testId ?? AsyncDropdownTestIds.Component}>
      <label htmlFor={name || id}>
        {label && (
          <Label
            $isDisabled={variant === "DISABLED" || variant === "READONLY"}
            $hasError={hasError}
            id={labelId}
          >
            {label} {isRequired ? <Asterisk>*</Asterisk> : null}
          </Label>
        )}
        <DropdownContextProvider
          allowIcons={allowIcons}
          hasError={hasError}
          variant={variant}
          hasSelectedOption={hasSelectedOption}
        >
          {children}
        </DropdownContextProvider>
      </label>
      {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </div>
  );
};

type MultiSelectAsyncDropdownProps = MultiSelectDropdownProps & {
  getOptions: (inputValue: string) => Promise<Option[]>;
};

export const MultiSelectAsyncDropdown: FC<MultiSelectAsyncDropdownProps> =
  forwardRef(
    (
      {
        id,
        testId,
        label,
        labelId,
        variant = "DEFAULT",
        isRequired = false,
        errorMessage = "",
        allowIcons = false,
        options,
        defaultValue,
        value,
        name = "",
        onSelect,
        onDeselect,
        onClear,
        placeholder = "Select...",
        menuPlacement,
        maxOptionsToShow = 8,
        size = "NORMAL",
        getOptions,
        defaultOptions,
        styles,
        ...selectProps
      },
      ref: ForwardedRef<ElementRef<typeof AsyncSelect>>
    ) => {
      const [generatedId] = useState(_uniqueId("single-select-"));
      const { selected, setSelected, clearSelected, dropdownProps } =
        useDropdown({
          defaultValue,
          value,
          name: name || id || generatedId,
          onClear,
          options,
          placeholder,
          variant,
          menuPlacement,
          size,
          ...selectProps,
        });

      const hasSelectedOption = !!selected;

      const {
        pillContainerRef,
        resetHiddenPillLabels,
        renderPillCounter,
        hiddenPillLabels,
      } = useCounterPill();

      useEffect(() => {
        renderPillCounter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
      useEffect(() => {
        // We debounce the resize event to avoid re-rendering
        // the pill on every resize pixel event.
        const debouncedHandleResize = _debounce(() => {
          if (!dropdownProps.menuIsOpen) {
            renderPillCounter();
          }
        }, 250);

        window.addEventListener("resize", debouncedHandleResize);

        return () => {
          window.removeEventListener("resize", debouncedHandleResize);
        };
      }, [renderPillCounter, dropdownProps.menuIsOpen]);

      const handleDeselectOption = (option: Option) => {
        setSelected((prevSelected) =>
          prevSelected?.filter((selectedOption) => selectedOption !== option)
        );
        onDeselect({ deselected: option });
      };

      const handleSelectOption = (option: Option) => {
        setSelected((prevSelected) => [...(prevSelected || []), option]);
        onSelect({ selected: option });
      };
      const loadOptions = async (inputValue: string) => getOptions(inputValue);

      return (
        <AsyncDropdownContainer
          id={id ?? generatedId}
          name={name}
          testId={testId}
          label={label}
          labelId={labelId}
          variant={variant}
          errorMessage={errorMessage}
          isRequired={isRequired}
          allowIcons={allowIcons}
          hasSelectedOption={hasSelectedOption}
        >
          <AsyncSelect
            {...dropdownProps}
            valueContainerChildRef={pillContainerRef}
            hiddenPillLabels={hiddenPillLabels}
            ref={ref}
            id={id}
            name={name}
            isMulti
            closeMenuOnSelect={false}
            components={multiSelectOverrideComponents}
            cacheOptions={true}
            value={value}
            loadOptions={loadOptions}
            maxMenuHeight={getOptionsMaxHeight(
              maxOptionsToShow,
              defaultOptions
            )}
            onInputChange={() => {
              // Action can be 'input-blur' or 'menu-close'
              // When blurring both events get dispatched
              // We have to care about recalculating the hidden pills
              // in both cases.
              renderPillCounter();
            }}
            onChange={(value, action) => {
              if (value && action.action === "select-option") {
                const option = action.option as Option;
                if (selected?.includes(option)) {
                  handleDeselectOption(option);
                } else {
                  handleSelectOption(option);
                }
              }
              // triggered when the user clicks on the x icon
              // on the pills from the stack container
              if (action.action === "deselect-option") {
                const option = action.option as Option;
                handleDeselectOption(option);
              }

              if (onClear && action.action === "clear") {
                clearSelected();
                onClear({ cleared: action.removedValues as Option[] });
                resetHiddenPillLabels();
              }
            }}
            styles={styles}
            {...(Boolean(defaultOptions) && { defaultOptions })}
          />
        </AsyncDropdownContainer>
      );
    }
  );
